import React, { useState } from 'react';
import Title from 'components/UI/Title';
import SignUpMemberForm from 'components/Forms/Members/SignUpMemberForm';
import SignUpTravelAgencyForm from 'components/Forms/TravelAgencies/SignUpTravelAgencyForm';
import Stepper from 'components/UI/Stepper';
import Row from 'components/UI/Layout/Row';
import ActionsRepository from 'services/repositories/ActionsRepository';
import { get, isNil } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';

function SignUpPage({ isB2cPage })
{
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(0);
    const [finalStepMessage, setFinalStepMessage] = useState('default');
    const [signUpForm, setSignUpForm] = useState({
        travelAgency: {},
        travelAgent: {},
    });
    const externalRewardType = searchParams.get('rafd');
    const referrerExternalId = searchParams.get('external_reward');

    const steps = [
        {
            id: '1',
            title: 'Travel Agency',
        },
        {
            id: '2',
            title: 'Travel Agent',
        },
        {
            id: '3',
            title: 'Set Password',
        },
    ];

    const sendInfoToBDM = async (form) =>
    {
        const data = {
            travelAgency: form.travelAgency,
            member: form.travelAgent,
            referrerExternalId,
        };

        const response = await (new ActionsRepository()).requestRegistration(data);

        if (!response.isSuccess())
        {
            return false;
        }

        setFinalStepMessage(form.travelAgent.is_signed_up ? 'ALREADY_SETUP' : 'SETUP_PASSWORD');
        return true;
    };

    const sendAccountSetupInstructions = async (form) =>
    {
        const data = {
            travelAgency: form.travelAgency,
            member: form.travelAgent,
        };
        const response = await (new ActionsRepository()).sendAccountSetup(data);
        const responseData = response.getData()?.data;

        if (!response.isSuccess() && responseData && responseData?.error)
        {
            setFinalStepMessage('ALREADY_SETUP');
            return false;
        }
        if (!response.isSuccess())
        {
            return false;
        }

        setFinalStepMessage('SETUP_PASSWORD');
        return true;
    };

    const travelAgencyMismatch = (form) =>
    {
        if (!form.travelAgent.travelAgency)
        {
            return false;
        }

        const compareNumber = form.travelAgent.travelAgency[form.travelAgency.identifier_type];
        const compareNumberParent = get(form.travelAgent, `travelAgency.parent.${form.travelAgency.identifier_type}`);

        const mismatch = Boolean(!compareNumber || compareNumber !== form.travelAgency.identifier_number);
        const mismatchParent = Boolean(!compareNumberParent || compareNumberParent !== form.travelAgency.identifier_number);

        return (
            form.travelAgent.id
            && (mismatch && mismatchParent)
        );
    };

    const travelAgencyNextStepAction = (form) =>
    {
        setSignUpForm(form);
        return nextStep();
    };

    const travelAgentNextStepAction = async (form) =>
    {
        setSignUpForm(form);

        /**
         * Account is already setup
         */
        if (!isNil(form.travelAgent.username) && !travelAgencyMismatch(form) && form.travelAgent?.is_signed_up)
        {
            setFinalStepMessage('ALREADY_SETUP');
            return nextStep();
        }

        /**
         * Agent was fetched, but its travel agency does not match the input travel agency number or member
         * is a direct client
         */
        if (travelAgencyMismatch(form) || (form.travelAgent.id && !form.travelAgent.travelAgency && form.travelAgent.is_signed_up))
        {
            const sendToBdmResponse = await sendInfoToBDM(form);

            if (!sendToBdmResponse) return false;

            return nextStep();
        }

        /**
         * Send account setup instructions.
         */
        if (
            form.travelAgent.id
            && form.travelAgency.id
            && form.travelAgent.email
            && form.travelAgent.is_signed_up)
        {
            const response = await sendAccountSetupInstructions(form);
            if (!response) return false;
            return nextStep();
        }

        /**
         * Send info to BDM
         */
        if (!form.travelAgent.id || form.travelAgent?.is_signed_up === false)
        {
            const response = await sendInfoToBDM(form);
            if (!response) return false;

            return nextStep();
        }

        return false;
    };

    const nextStep = () =>
    {
        setStep(step + 1);
    };

    return (
        <div className="tw-flex tw-flex-col tw-min-h-screen tw-items-center tw-justify-center tw-w-full tw-overflow-y-auto tw-box-content">

            <Title title="Create an account" className="primary" />

            <div className="tw-flex tw-flex-col dark:tw-bg-neutral-700 tw-bg-white tw-rounded tw-p-3 lg:tw-w-2/5 md:tw-w-2/3">

                <div className="tw-flex-col">

                    <Stepper activeStep={step} steps={steps} />

                    {(() =>
                    {
                        switch (step)
                        {
                            case 0:
                                return (
                                    <SignUpTravelAgencyForm
                                        nextStep={travelAgencyNextStepAction}
                                        formData={signUpForm}
                                    />
                                );
                            case 1:
                                return (
                                    <SignUpMemberForm
                                        nextStep={travelAgentNextStepAction}
                                        formData={signUpForm}
                                        externalRewardType={externalRewardType}
                                    />
                                );
                            default:
                                return (
                                    <div className="tw-p-6">
                                        { finalStepMessage === 'SETUP_PASSWORD'
                                            && (
                                                <Row className="tw-mb-3 tw-mt-3 tw-justify-center">
                                                    <p>
                                                        <strong>Dear {signUpForm.travelAgent.first_name},</strong>
                                                        <br />
                                                        <br />

                                                        Your user has been created, we have sent you an email with instructions on how to setup your password.
                                                    </p>
                                                </Row>
                                            )}

                                        { finalStepMessage === 'SENT_TO_BDM'
                                            && (
                                                <Row className="tw-mb-3 tw-mt-3 tw-justify-center">
                                                    <p>
                                                        <strong>Dear {signUpForm.travelAgent.first_name},</strong>
                                                        <br />
                                                        <br />

                                                        We&apos;d like to inform you that we received your information. Our Business
                                                        Development team is currently receiving your information and will be
                                                        planning a call with you shortly.<br /><br />
                                                        Thank you, again, for taking the time to contact us with your request.<br />
                                                        Best regards.
                                                    </p>
                                                </Row>
                                            )}

                                        { finalStepMessage === 'ALREADY_SETUP'
                                            && (
                                                <Row className="tw-mb-3 tw-mt-3 tw-justify-center">
                                                    <p>
                                                        <strong>Dear {signUpForm.travelAgent.first_name},</strong>
                                                        <br />

                                                        It seems you&apos;ve already setup an account with us,
                                                        if you don&apos;t remember your password,
                                                        use <a className="tw-text-primary-link hover:tw-text-primary-link-hover" href="/forgot-password">this link to reset it</a> or <a className="tw-text-primary-link hover:tw-text-primary-link-hover" href="/forgot-password">go the login page</a>.

                                                    </p>
                                                </Row>
                                            )}
                                    </div>
                                );
                        }
                    })()}

                </div>
            </div>

            <Helmet title={`Sign Up | ${isB2cPage ? 'Luxuria Vacations' : 'UJV'}`}>
                <meta name="description" content="Sign Up" />
            </Helmet>
        </div>
    );
}

SignUpPage.defaultProps = {
    isB2cPage: false,
};

SignUpPage.propTypes = {
    isB2cPage: PropTypes.bool,
};

export default SignUpPage;
